import React, { useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useQuery, useMutation } from '@apollo/client';
import { Stack, CircularProgress, Divider, Typography } from '@mui/material';
import ActionsDrawer from '../drawer/ActionsDrawer';
import DrawerHeader from '../drawer/DrawerHeader';
import {
  GET_COMPANY_CONTRACT_GENERATION,
  GENERATE_COMPANY_FRAMEWORK_CONTRACT,
  CONTRACT_MASTER_ENTITIES,
} from '../../graphql';
import { useSnackBars, usePlatformFeatures } from '../../hooks';
import useFetchPresignedUrl from '../../hooks/useFetchPresignedUrl';
import useLegalRepresentatives from '../../hooks/useLegalRepresentatives';
import useEndorsements from '../../hooks/useEndorsements';
import { PLATFORM_FEATURES } from '../../constants/platform-features';
import { DEFAULT_APPROVED_CREDIT_LINE_AMOUNT } from '../../constants/contract-types';
import LegalRepresentativesComponent from './LegalRepresentativesComponent';
import ContractInfoComponent from './ContractInfoComponent';
import EndorsementComponent from './EndorsementComponent';
import ContinueButtonComponent from './ContinueButtonComponent';
import HandleResponse from './HandleResponse';
import ContractRestriction from './ContractRestriction';
import LegalReportPowers from './LegalReportPowers';
import PersoneriaCommentsComponent from './PersoneriaCommentsComponent';

const GenerateContractDialog = ({ open, toggleDialog, companyId }) => {
  const { addAlert } = useSnackBars();
  const { isFeatureEnabled } = usePlatformFeatures();
  const showCreditLine = isFeatureEnabled(PLATFORM_FEATURES.SHOW_CONTRACT_CREDIT_LINE);
  const [contractInfo, setContractInfo] = useState({});
  const [relatedContractRequest, setRelatedContractRequest] = useState({});
  const [availableEndorsements, setAvailableEndorsements] = useState([]);
  const [availableRepresentatives, setAvailableRepresentatives] = useState([]);
  const [legalReport, setLegalReport] = useState({});
  const [personeriaComments, setPersoneriaComments] = useState({ personeriaComments: '' });
  const [approvedCreditLineAmount, setApprovedCreditLineAmount] = useState(
    DEFAULT_APPROVED_CREDIT_LINE_AMOUNT,
  );
  const hasLoadedRef = useRef(false);

  const {
    legalRepresentatives,
    addLegalRepresentative,
    removeLegalRepresentative,
    updateLegalRepresentative,
  } = useLegalRepresentatives();
  const { endorsements, addEndorsement, removeEndorsement, updateEndorsement } = useEndorsements(
    availableEndorsements,
    relatedContractRequest?.restriction?.endorser?.relatedPerson?.displayNationalIdentifier,
  );
  const [,, fetchGivenId] = useFetchPresignedUrl();

  const openUrl = (globalAppId) => fetchGivenId(globalAppId);

  const { loading } = useQuery(GET_COMPANY_CONTRACT_GENERATION, {
    variables: { companyId },
    notifyOnNetworkStatusChange: true,
    skip: !open,
    onCompleted: (data) => {
      if (hasLoadedRef.current) return;
      hasLoadedRef.current = true;
      HandleResponse(
        data.company,
        setContractInfo,
        setAvailableRepresentatives,
        setAvailableEndorsements,
        setRelatedContractRequest,
        setLegalReport,
        setPersoneriaComments,
        setApprovedCreditLineAmount,
      );
    },
  });

  const finalContractInfo = useMemo(() => {
    const { mainDisabled, contractTypeLocked, ...rest } = contractInfo;
    return rest;
  }, [contractInfo]);

  const neededLegalRepresentativesProps = useMemo(() => [
    'name',
    'rut',
    'nationality',
    'profession',
    'email',
    'civilStatus',
    'label',
  ], []);

  const filteredPropsLegalRepresentatives = useMemo(() => legalRepresentatives.map((rep) => {
    const filteredRep = Object.keys(rep).reduce(
      (acc, key) => {
        if (neededLegalRepresentativesProps.includes(key)) {
          acc[key] = rep[key];
        }
        return acc;
      },
      {},
    );
    return filteredRep;
  }), [legalRepresentatives, neededLegalRepresentativesProps]);

  const neededEndorsementsProps = useMemo(() => [
    'name',
    'rut',
    'nationality',
    'civilStatus',
    'profession',
    'address',
    'email',
    'label',
    'spouceName',
    'spouceRut',
    'spouceAddress',
    'spouceLabel',
    'spouceNationality',
    'spouceProfession',
    'spouceMasterEntityId',
    'masterEntityId',
    'personeriaComments',
    'spouceEmail',
  ], []);

  const personeriaCommentsValue = personeriaComments.personeriaComments || '';

  const filteredPropsEndorsements = useMemo(() => endorsements.map((endorsement) => {
    const filteredEndorsement = Object.keys(endorsement).reduce(
      (acc, key) => {
        if (neededEndorsementsProps.includes(key)) {
          acc[key] = endorsement[key];
        }
        return acc;
      },
      {},
    );
    return filteredEndorsement;
  }), [endorsements, neededEndorsementsProps]);

  const [generateCompanyFrameworkContract, { loading: generateLoading }] = useMutation(
    GENERATE_COMPANY_FRAMEWORK_CONTRACT,
    {
      variables: {
        companyId,
        contractInfo: finalContractInfo,
        legalRepresentatives: filteredPropsLegalRepresentatives,
        endorsements: filteredPropsEndorsements,
        personeriaComments: personeriaCommentsValue,
        contractRequestId: relatedContractRequest.id,
      },
      refetchQueries: [CONTRACT_MASTER_ENTITIES],
      onCompleted: (data) => {
        openUrl(data.generateCompanyFrameworkContract.frameworkContract.globalAppId);
      },
      onError: (error) => addAlert({
        id: 'generate-contract-error',
        severity: 'error',
        message: `Error: ${error.message}`,
      }),
    },
  );

  const handleSetOpen = (value) => {
    if (!value) {
      hasLoadedRef.current = false;
      toggleDialog();
    }
  };

  if (loading) {
    return (
      <ActionsDrawer
        title="Generación de Contrato"
        width={900}
        open={open}
        setOpen={handleSetOpen}
        headerComponent={DrawerHeader}
        titleColor="gray.A700"
        loading={loading}
        paperSx={{
          borderTopLeftRadius: 24,
          borderBottomLeftRadius: 24,
        }}
      >
        <CircularProgress />
      </ActionsDrawer>
    );
  }

  return (
    <ActionsDrawer
      title="Generación de Contrato"
      width={900}
      open={open}
      setOpen={handleSetOpen}
      headerComponent={DrawerHeader}
      titleColor="gray.A700"
      loading={loading}
      paperSx={{
        borderTopLeftRadius: 24,
        borderBottomLeftRadius: 24,
      }}
    >
      <Stack spacing={3} sx={{ width: '100%' }}>
        <Stack direction="column" spacing={2} sx={{ flex: 1, minWidth: 0 }}>
          {(relatedContractRequest?.restriction || showCreditLine) && (
            <ContractRestriction
              contractRestriction={relatedContractRequest.restriction}
              showCreditLine={showCreditLine}
              approvedCreditLineAmount={approvedCreditLineAmount}
            />
          )}
          {legalReport?.signatureCount > 0 && (
            <LegalReportPowers
              creditOperationsAvailable={legalReport?.creditOperationsAvailable}
              factoringAvailable={legalReport?.factoringAvailable}
              signatureCount={legalReport?.signatureCount}
            />
          )}

          <Divider>
            <Typography variant="body2" color="text.secondary">
              Formulario de contrato
            </Typography>
          </Divider>
          <ContractInfoComponent
            contractInfo={contractInfo}
            setContractInfo={setContractInfo}
          />
          <LegalRepresentativesComponent
            legalRepresentatives={legalRepresentatives}
            addLegalRepresentative={addLegalRepresentative}
            removeLegalRepresentative={removeLegalRepresentative}
            updateLegalRepresentative={updateLegalRepresentative}
            availableRepresentatives={availableRepresentatives}
          />
          <PersoneriaCommentsComponent
            personeriaComments={personeriaComments}
            setPersoneriaComments={setPersoneriaComments}
          />
          <EndorsementComponent
            endorsements={endorsements}
            addEndorsement={addEndorsement}
            removeEndorsement={removeEndorsement}
            updateEndorsement={updateEndorsement}
            availableEndorsements={availableEndorsements}
            mandatoryEndorsementRut={relatedContractRequest?.restriction?.endorser
              ?.relatedPerson?.displayNationalIdentifier}
          />
        </Stack>

        <Stack direction="row" justifyContent="flex-end">
          <ContinueButtonComponent
            contractInfo={contractInfo}
            legalRepresentatives={legalRepresentatives}
            endorsements={endorsements}
            generateCompanyFrameworkContract={generateCompanyFrameworkContract}
            generateLoading={generateLoading}
          />
        </Stack>
      </Stack>
    </ActionsDrawer>
  );
};

GenerateContractDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  toggleDialog: PropTypes.func.isRequired,
  companyId: PropTypes.string.isRequired,
};

export default GenerateContractDialog;
